import React from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../common/types';
import { useDeleteSmartLinkFlow } from '../../hooks';
import { useUnpublishPageFlow } from '../../unpublishPage/useUnpublishPageFlow';
import { SMLUnpublishPageModal } from '../../unpublishPage/SMLUnpublishPageModal';
import { useEditorContext } from '../SMLEditorContext';
import { EditorStatus } from '../topBar/editorStatus';
import { EditorPublish } from '../topBar/useEditorPublish';
import { SMLEditorPublishButton } from '../topBar/SMLEditorPublishButton';
import { pageMessages as m } from './pageMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for several parts; each part uses its own slice of it */
import styles from './SMLPageTab.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface SMLPageStatusCardProps {
  status: EditorStatus;
  publish: EditorPublish;
  blockCount: number;
}

/** The status of the page (design a6): live since, changes that wait, unpublish, delete (existing dialogs). */
const SMLPageStatusCardComponent: React.FC<SMLPageStatusCardProps> = ({ status, publish, blockCount }) => {
  const intl = useIntl();
  const { smartLink, accountId, smartLinkId, isBioLink } = useEditorContext();
  const unpublish = useUnpublishPageFlow({ accountId, smartLinkId });
  const { openSmartLinkDeletionModal, SmartLinkDeletionModal } = useDeleteSmartLinkFlow(
    accountId,
    smartLinkId,
    smartLink
  );
  const publishedAt = smartLink.publishedSmartLinkVersion?.publishedAt ?? null;

  let line = intl.formatMessage(m.notLiveHelp);
  if (status.kind === 'upToDate') {
    line = intl.formatMessage(m.upToDate);
  } else if (status.kind === 'pending') {
    line =
      status.count === null
        ? intl.formatMessage(m.pendingNoCount)
        : intl.formatMessage(m.pending, { count: status.count });
  }

  return (
    <section className={styles.card} data-testid="page-status-card">
      <h4 className={styles.cardTitle}>{intl.formatMessage(m.status)}</h4>
      <div className={styles.statusRow}>
        <span className={styles.statusText}>
          <b>
            {status.kind === 'draft' || publishedAt === null
              ? intl.formatMessage(m.notLive)
              : intl.formatMessage(m.liveSince, {
                  date: intl.formatDate(publishedAt, { day: 'numeric', month: 'short' }),
                })}
          </b>
          <small>{line}</small>
        </span>
        <SMLEditorPublishButton accountId={accountId} status={status} blockCount={blockCount} publish={publish} />
      </div>
      {status.kind !== 'draft' && (
        <div className={styles.statusRow}>
          <span className={styles.statusText}>
            <b>{intl.formatMessage(m.offline)}</b>
            <small>{intl.formatMessage(m.offlineHelp)}</small>
          </span>
          <button type="button" className={styles.secondary} onClick={unpublish.openModal}>
            {intl.formatMessage(m.unpublish)}
          </button>
        </div>
      )}
      {!isBioLink && (
        <div className={styles.statusRow}>
          <span className={styles.statusText}>
            <b>{intl.formatMessage(m.delete)}</b>
            <small>{intl.formatMessage(m.deleteHelp)}</small>
          </span>
          <button type="button" className={styles.danger} onClick={openSmartLinkDeletionModal}>
            {intl.formatMessage(m.deleteButton)}
          </button>
        </div>
      )}
      <SMLUnpublishPageModal
        isOpen={unpublish.isModalOpen}
        accountId={accountId}
        smartLink={smartLink}
        isUnpublishing={unpublish.isUnpublishing}
        closeModal={unpublish.closeModal}
        onUnpublish={unpublish.unpublish}
      />
      {SmartLinkDeletionModal}
    </section>
  );
};

export const SMLPageStatusCard = TypedMemo(SMLPageStatusCardComponent);
